import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    FormControl,
    Grid,
    InputLabel,
    Paper,
    Select,
    MenuItem,
    Stack,
    Typography,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip
} from '@mui/material';
import {
    Print as IconPrint,
    PictureAsPdf as IconPdf,
    TableView as IconExcel,
    Description as IconWord,
    ArrowBack as IconVolver,
    TrendingUp as IconTrendingUp,
    CheckCircle as IconCheck,
    Speed as IconSpeed,
    Timeline as IconTimeline
} from '@mui/icons-material';

import FiltroFilial from '../../components/common/FiltroFilial';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';
import { getsTerapiaPlanResumenView, getsTerapiaPlanAvanceView } from '../../services/terapiaPlanService';
import GraficoCurvaEficacia from '../../components/common/GraficoCurvaEficacia';
import { exportarExcel, exportarPdf, exportarWord, imprimirReporte } from '../../services/reporteExportService';
import { registrarEjecucionReporte } from '../../services/bitacoraReporteService';

const CurvaEficacia = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const idPlanParam = parseInt(searchParams.get('idPlan') || searchParams.get('idTerapiaPlan') || '0', 10);

    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [listPlanes, setListPlanes] = useState([]);
    const [idTerapiaPlan, setIdTerapiaPlan] = useState(idPlanParam);
    const [rows, setRows] = useState([]);
    const [cargandoPlanes, setCargandoPlanes] = useState(false);
    const [cargandoAvance, setCargandoAvance] = useState(false);

    // Cargar lista de planes de terapia según la filial
    const fetchPlanes = useCallback(async () => {
        setCargandoPlanes(true);
        try {
            const data = await getsTerapiaPlanResumenView({
                idFilial: idFilial || 0,
                member: '',
                therapist: '',
                idEstadoVigencia: 0,
                idEstado: 0,
                idTerapeuta: 0
            });
            const validos = (data || []).filter((row) => row.idTerapiaPlan != null);
            setListPlanes(validos);
        } catch (err) {
            console.error('Error cargando planes:', err);
        } finally {
            setCargandoPlanes(false);
        }
    }, [idFilial]);

    useEffect(() => {
        fetchPlanes();
    }, [fetchPlanes]);

    // Cargar datos de avance del plan seleccionado
    const fetchAvance = useCallback(async (idPlan) => {
        if (!idPlan || idPlan <= 0) {
            setRows([]);
            return;
        }

        setCargandoAvance(true);
        const t0 = performance.now();
        try {
            const data = await getsTerapiaPlanAvanceView(idPlan);
            setRows(data || []);
            registrarEjecucionReporte({
                reportPath: '/reporte/curva-eficacia',
                format: 'HTML',
                reportAction: 'Render',
                timeRendering: Math.round(performance.now() - t0),
                rowCount: data?.length || 0,
                status: 'rsSuccess'
            });
        } catch (err) {
            console.error('Error cargando avance:', err);
        } finally {
            setCargandoAvance(false);
        }
    }, []);

    // Sincronizar parámetro URL
    useEffect(() => {
        if (idPlanParam > 0) {
            setIdTerapiaPlan(idPlanParam);
            fetchAvance(idPlanParam);
        }
    }, [idPlanParam, fetchAvance]);

    const handleSelectPlan = (nuevoId) => {
        const id = parseInt(nuevoId, 10) || 0;
        setIdTerapiaPlan(id);
        if (id > 0) {
            setSearchParams({ idPlan: id });
            fetchAvance(id);
        } else {
            setSearchParams({});
            setRows([]);
        }
    };

    // Procesamiento estadístico: agrupar por sesión cronológica y calcular regresión
    const { sesionesPuntos, estadisticas, planInfo } = useMemo(() => {
        if (!rows || rows.length === 0) {
            return { sesionesPuntos: [], estadisticas: null, planInfo: null };
        }

        const primerItem = rows[0];
        const planInfoObj = {
            participante: primerItem.participante,
            terapeuta: primerItem.terapeuta,
            modelo: primerItem.modelo,
            servicio: primerItem.tipo || primerItem.codigoServicio,
            fechaInicio: primerItem.fechaInicio,
            fechaFin: primerItem.fechaFin
        };

        // Mapa de sesiones únicas ordenadas por fecha
        const mapaSesiones = new Map();
        for (const r of rows) {
            if (r.idSesion != null) {
                if (!mapaSesiones.has(r.idSesion)) {
                    mapaSesiones.set(r.idSesion, {
                        idSesion: r.idSesion,
                        fechaSesion: r.fechaSesion,
                        puntuacionTotal: 0,
                        criteriosTotal: 0
                    });
                }
                const s = mapaSesiones.get(r.idSesion);
                s.puntuacionTotal += r.puntuacionSesion || 0;
                s.criteriosTotal += r.criteriosPuntuados || 0;
            }
        }

        const ordenadas = Array.from(mapaSesiones.values()).sort(
            (a, b) => new Date(a.fechaSesion) - new Date(b.fechaSesion)
        );

        if (ordenadas.length === 0) {
            return { sesionesPuntos: [], estadisticas: null, planInfo: planInfoObj };
        }

        // Determinar escala o puntaje máximo esperado por sesión
        const maxPuntajeObservado = Math.max(...ordenadas.map((s) => s.puntuacionTotal), 1);
        const metaPuntaje = Math.max(maxPuntajeObservado, 10);

        // Construir puntos con porcentaje de logro progresivo
        let sumaPuntajes = 0;
        const totalEstimadoPlan = metaPuntaje * ordenadas.length;

        const puntos = ordenadas.map((s, idx) => {
            sumaPuntajes += s.puntuacionTotal;
            const porcentajeSesion = Math.min(100, (s.puntuacionTotal / metaPuntaje) * 100);
            const porcentajeAcumulado = Math.min(100, (sumaPuntajes / totalEstimadoPlan) * 100);
            // Ponderación de avance clínico (70% acumulado + 30% desempeño de sesión)
            const valorCalculado = Number((porcentajeAcumulado * 0.7 + porcentajeSesion * 0.3).toFixed(1));

            return {
                sesionNum: idx + 1,
                idSesion: s.idSesion,
                fecha: s.fechaSesion,
                fechaStr: s.fechaSesion ? new Date(s.fechaSesion).toLocaleDateString('es-PE') : `S-${idx + 1}`,
                puntaje: s.puntuacionTotal,
                criterios: s.criteriosTotal,
                porcentaje: valorCalculado
            };
        });

        // Cálculo de Regresión Lineal (Mínimos Cuadrados): y = mx + b
        const N = puntos.length;
        const sumX = puntos.reduce((acc, p) => acc + p.sesionNum, 0);
        const sumY = puntos.reduce((acc, p) => acc + p.porcentaje, 0);
        const meanX = sumX / N;
        const meanY = sumY / N;

        let numPendiente = 0;
        let denPendiente = 0;
        let ssTot = 0;

        for (const p of puntos) {
            numPendiente += (p.sesionNum - meanX) * (p.porcentaje - meanY);
            denPendiente += Math.pow(p.sesionNum - meanX, 2);
            ssTot += Math.pow(p.porcentaje - meanY, 2);
        }

        const m = denPendiente !== 0 ? numPendiente / denPendiente : 0;
        const b = meanY - m * meanX;

        // Calcular R²
        let ssRes = 0;
        for (const p of puntos) {
            const yPred = m * p.sesionNum + b;
            p.porcentajeTendencia = Number(Math.max(0, Math.min(100, yPred)).toFixed(1));
            ssRes += Math.pow(p.porcentaje - yPred, 2);
        }

        const r2 = ssTot !== 0 ? Math.max(0, Math.min(1, 1 - ssRes / ssTot)) : 1;
        const proyeccion100 = m > 0 ? Math.max(N, Math.ceil((100 - b) / m)) : null;

        const ultimoLogro = puntos[puntos.length - 1].porcentaje;

        return {
            sesionesPuntos: puntos,
            planInfo: planInfoObj,
            estadisticas: {
                totalSesiones: N,
                ultimoLogro,
                pendiente: Number(m.toFixed(2)),
                intercepto: Number(b.toFixed(2)),
                r2: Number(r2.toFixed(2)),
                proyeccion100
            }
        };
    }, [rows]);

    // Opciones de exportación
    const handleImprimir = () => {
        imprimirReporte('Curva de Eficacia Terapeutica - ' + (planInfo?.participante || ''));
    };

    const handlePdf = () => {
        exportarPdf('Curva_Eficacia_' + (planInfo?.participante || ''));
    };

    const handleExcel = () => {
        if (!sesionesPuntos || sesionesPuntos.length === 0) return;
        exportarExcel({
            titulo: 'Curva de Eficacia Terapéutica',
            subtitulo: `Participante: ${planInfo?.participante || ''} | Terapeuta: ${planInfo?.terapeuta || ''}`,
            indicadores: [
                { etiqueta: 'Total Sesiones', valor: estadisticas?.totalSesiones || 0 },
                { etiqueta: 'Eficacia Actual', valor: `${estadisticas?.ultimoLogro || 0}%` },
                { etiqueta: 'Tasa Avance / Sesión', valor: `+${estadisticas?.pendiente || 0}%` },
                { etiqueta: 'Consistencia R²', valor: estadisticas?.r2 || 0 }
            ],
            columnas: [
                { campo: 'sesionNum', titulo: 'N° Sesión' },
                { campo: 'fechaStr', titulo: 'Fecha' },
                { campo: 'criterios', titulo: 'Criterios Evaluados' },
                { campo: 'puntaje', titulo: 'Puntaje Obtenido' },
                { campo: 'porcentaje', titulo: '% Logro Real' },
                { campo: 'porcentajeTendencia', titulo: '% Tendencia' }
            ],
            filas: sesionesPuntos
        });
    };

    const handleWord = () => {
        if (!sesionesPuntos || sesionesPuntos.length === 0) return;
        exportarWord({
            titulo: 'Curva de Eficacia Terapéutica',
            subtitulo: `Participante: ${planInfo?.participante || ''} | Terapeuta: ${planInfo?.terapeuta || ''}`,
            indicadores: [
                { etiqueta: 'Total Sesiones', valor: estadisticas?.totalSesiones || 0 },
                { etiqueta: 'Eficacia Actual', valor: `${estadisticas?.ultimoLogro || 0}%` },
                { etiqueta: 'Tasa Avance / Sesión', valor: `+${estadisticas?.pendiente || 0}%` },
                { etiqueta: 'Consistencia R²', valor: estadisticas?.r2 || 0 }
            ],
            columnas: [
                { campo: 'sesionNum', titulo: 'N° Sesión' },
                { campo: 'fechaStr', titulo: 'Fecha' },
                { campo: 'criterios', titulo: 'Criterios Evaluados' },
                { campo: 'puntaje', titulo: 'Puntaje' },
                { campo: 'porcentaje', titulo: '% Logro' },
                { campo: 'porcentajeTendencia', titulo: '% Tendencia' }
            ],
            filas: sesionesPuntos
        });
    };

    const textoPlan = (row) =>
        `${row.participante || 'Participante'} (${row.tipo || row.codigoServicio || 'Terapia'}) [${row.fechaInicio ? new Date(row.fechaInicio).toLocaleDateString('es-PE') : ''} - ${row.fechaFin ? new Date(row.fechaFin).toLocaleDateString('es-PE') : ''}]`;

    return (
        <Box className="area-reporte" sx={{ p: { xs: 1.5, sm: 3 } }}>
            {/* CABECERA Y FILTROS */}
            <Box className="no-print" sx={{ mb: 3 }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} md={7}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Button component={Link} to="/reporte" variant="outlined" size="small" startIcon={<IconVolver />}>
                                Catálogo
                            </Button>
                            <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
                                Curva de Eficacia Terapéutica
                            </Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Correlación de esfuerzo clínico (N° de sesiones asistidas) frente a la tasa de logro de objetivos.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Button variant="contained" size="small" startIcon={<IconPrint />} onClick={handleImprimir} disabled={!estadisticas}>
                                Imprimir
                            </Button>
                            <Button variant="outlined" color="error" size="small" startIcon={<IconPdf />} onClick={handlePdf} disabled={!estadisticas}>
                                PDF
                            </Button>
                            <Button variant="outlined" color="success" size="small" startIcon={<IconExcel />} onClick={handleExcel} disabled={!estadisticas}>
                                Excel
                            </Button>
                            <Button variant="outlined" color="info" size="small" startIcon={<IconWord />} onClick={handleWord} disabled={!estadisticas}>
                                Word
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>

                {/* FILTROS DE SELECCIÓN */}
                <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: '#f8f9fa' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4} md={3}>
                            <FiltroFilial
                                filiales={filiales}
                                idFilial={idFilial}
                                bloqueado={bloqueado}
                                onChange={(nueva) => {
                                    setIdFilial(nueva);
                                    handleSelectPlan(0);
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={8} md={9}>
                            <FormControl fullWidth size="small">
                                <InputLabel htmlFor="select-plan-curva" shrink>
                                    Plan de Terapia del Participante
                                </InputLabel>
                                <Select
                                    id="select-plan-curva"
                                    value={idTerapiaPlan}
                                    label="Plan de Terapia del Participante"
                                    onChange={(e) => handleSelectPlan(e.target.value)}
                                    displayEmpty
                                >
                                    <MenuItem value={0}>{"(Seleccione un plan de terapia)"}</MenuItem>
                                    {listPlanes.map((plan) => (
                                        <MenuItem key={plan.idTerapiaPlan} value={plan.idTerapiaPlan}>
                                            {textoPlan(plan)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            {/* ESTADO DE CARGA */}
            {cargandoAvance && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {/* AVISO SIN PLAN SELECCIONADO */}
            {!idTerapiaPlan && !cargandoAvance && (
                <Card sx={{ p: 5, textAlign: 'center', bgcolor: '#fafafa', border: '1px dashed #ccc' }} className="no-print">
                    <IconTrendingUp sx={{ fontSize: 60, color: 'text.secondary', mb: 1 }} />
                    <Typography variant="h6" color="text.secondary">
                        Seleccione un plan de terapia arriba para analizar la curva de eficacia
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Podrá ver el ritmo de aprendizaje del participante, el gráfico de regresión lineal y la proyección de sesiones.
                    </Typography>
                </Card>
            )}

            {/* CONTENIDO DEL REPORTE */}
            {estadisticas && !cargandoAvance && (
                <Box>
                    {/* ENCABEZADO PARA IMPRESIÓN */}
                    <Box sx={{ display: 'none', '@media print': { display: 'block', mb: 2, textAlign: 'center' } }}>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            KOLOB - CENTRO TERAPÉUTICO
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            INFORME CLÍNICO DE EFICACIA TERAPÉUTICA
                        </Typography>
                        <Typography variant="body2">
                            Participante: <strong>{planInfo?.participante}</strong> | Terapeuta: <strong>{planInfo?.terapeuta}</strong>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Generado el {new Date().toLocaleDateString('es-PE')} a las {new Date().toLocaleTimeString('es-PE')}
                        </Typography>
                    </Box>

                    {/* TARJETAS DE INDICADORES (KPIS) */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined" sx={{ p: 2, borderLeft: '4px solid #1976d2' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconTimeline sx={{ color: '#1976d2' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        SESIONES ASISTIDAS
                                    </Typography>
                                </Stack>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                                    {estadisticas.totalSesiones}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Muestra correlativa analizada
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined" sx={{ p: 2, borderLeft: '4px solid #2e7d32' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconCheck sx={{ color: '#2e7d32' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        LOGRO ACTUAL
                                    </Typography>
                                </Stack>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: '#2e7d32' }}>
                                    {estadisticas.ultimoLogro}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Eficacia última sesión registrada
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined" sx={{ p: 2, borderLeft: '4px solid #5b2d8e' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconSpeed sx={{ color: '#5b2d8e' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        TASA DE EFICACIA
                                    </Typography>
                                </Stack>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: '#5b2d8e' }}>
                                    {estadisticas.pendiente >= 0 ? `+${estadisticas.pendiente}%` : `${estadisticas.pendiente}%`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Ganancia promedio por sesión
                                </Typography>
                            </Card>
                        </Grid>

                        <Grid item xs={6} sm={3}>
                            <Card variant="outlined" sx={{ p: 2, borderLeft: '4px solid #e07a1f' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <IconTrendingUp sx={{ color: '#e07a1f' }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                        CONSISTENCIA (R²)
                                    </Typography>
                                </Stack>
                                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1, color: '#e07a1f' }}>
                                    {estadisticas.r2}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {estadisticas.r2 >= 0.7 ? 'Progresión alta y predecible' : 'Avance con variabilidad'}
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* GRÁFICO DE CURVA DE EFICACIA */}
                    <Box sx={{ mb: 3 }}>
                        <GraficoCurvaEficacia
                            puntos={sesionesPuntos}
                            regresion={estadisticas}
                            titulo={`Curva de Progreso: ${planInfo?.participante || ''}`}
                            subtitulo={`Modelo: ${planInfo?.modelo || ''} | Terapeuta a cargo: ${planInfo?.terapeuta || ''}`}
                        />
                    </Box>

                    {/* CONCLUSIÓN Y DIAGNÓSTICO DEL ALGORITMO */}
                    <Alert
                        severity={estadisticas.pendiente > 1.5 ? 'success' : estadisticas.pendiente > 0 ? 'info' : 'warning'}
                        sx={{ mb: 3 }}
                    >
                        <strong>Diagnóstico de Eficacia Terapéutica:</strong>{' '}
                        {estadisticas.pendiente > 1.5 ? (
                            <span>
                                El participante muestra un <strong>ritmo de aprendizaje acelerado</strong> (+{estadisticas.pendiente}% por sesión) con alta estabilidad.
                                {estadisticas.proyeccion100 && (
                                    <span> A este ritmo, se proyecta el dominio pleno de los objetivos clínicos hacia la sesión N° <strong>{estadisticas.proyeccion100}</strong>.</span>
                                )}
                            </span>
                        ) : estadisticas.pendiente > 0 ? (
                            <span>
                                El participante mantiene un <strong>avance favorable constante</strong> (+{estadisticas.pendiente}% por sesión). Se sugiere consolidar los criterios en curso.
                            </span>
                        ) : (
                            <span>
                                Se detecta una <strong>meseta o estabilidad</strong> en las últimas sesiones. Se sugiere al terapeuta revisar el nivel de exigencia de los criterios o variar los reforzadores.
                            </span>
                        )}
                    </Alert>

                    {/* TABLA DETALLADA DE SESIONES */}
                    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderBottom: '1px solid #e0e0e0' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Detalle Cronológico de Sesiones Evaluadas
                            </Typography>
                        </Box>
                        <TableContainer sx={{ maxHeight: 380 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>N° Sesión</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>Fecha</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Criterios Puntuados</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Puntaje Sesión</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>% Logro Real</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>% Tendencia</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sesionesPuntos.map((s) => {
                                        const dif = s.porcentaje - (s.porcentajeTendencia || s.porcentaje);
                                        return (
                                            <TableRow key={'fila-sesion-' + s.sesionNum} hover>
                                                <TableCell align="center">
                                                    <strong>S-{s.sesionNum}</strong>
                                                </TableCell>
                                                <TableCell>{s.fechaStr}</TableCell>
                                                <TableCell align="center">{s.criterios}</TableCell>
                                                <TableCell align="center">{s.puntaje}</TableCell>
                                                <TableCell align="center">
                                                    <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                                                        {s.porcentaje}%
                                                    </span>
                                                </TableCell>
                                                <TableCell align="center" style={{ color: '#5b2d8e' }}>
                                                    {s.porcentajeTendencia}%
                                                </TableCell>
                                                <TableCell align="center">
                                                    {s.porcentaje >= 80 ? (
                                                        <Chip label="Logrado" color="success" size="small" />
                                                    ) : dif >= 0 ? (
                                                        <Chip label="Sobre media" color="primary" size="small" variant="outlined" />
                                                    ) : (
                                                        <Chip label="Bajo media" color="warning" size="small" variant="outlined" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            )}

            {/* ESTILOS DE IMPRESIÓN */}
            <style>
                {`
                @media print {
                    nav, header, aside, .no-print, .MuiAppBar-root, .MuiDrawer-root, #root > div > header {
                        display: none !important;
                    }
                    body, html {
                        background: #fff !important;
                        margin: 0 !important;
                        padding: 0 !important;
                    }
                    .area-reporte {
                        padding: 0 !important;
                    }
                    @page {
                        size: landscape;
                        margin: 12mm;
                    }
                }
                `}
            </style>
        </Box>
    );
};

export default CurvaEficacia;

